<script setup>
import { computed, nextTick, ref } from 'vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiModal from '@/components/ui/UiModal.vue'

/**
 * An icon chosen by sight: what is chosen shown as the icon, and a grid of the font's icons to
 * pick from — suggestions for things a catalogue has, else a search by name and meaning. The
 * font's words are English; common Polish words of the trade find their icons too.
 * v-model is the icon's class, e.g. "fa-display" (or null).
 */
const props = defineProps({ modelValue: { type: String, default: null }, disabled: Boolean })
const emit = defineEmits(['update:modelValue'])
const open = ref(false)
const query = ref('')
const icons = ref([])
const search = ref(null)

// What a catalogue, a sales team and a reception usually need.
const SUGGESTED = ['display', 'tablet-screen-button', 'desktop', 'computer', 'laptop', 'mobile-screen', 'print', 'video', 'camera', 'road-barrier', 'door-open', 'id-card', 'id-badge',
  'qrcode', 'barcode', 'fingerprint', 'key', 'lock', 'shield-halved', 'user-shield', 'bell-concierge', 'users', 'user-tie', 'truck', 'car', 'square-parking', 'building', 'industry',
  'warehouse', 'cloud', 'server', 'network-wired', 'wifi', 'plug', 'headset', 'screwdriver-wrench', 'toolbox', 'gears', 'graduation-cap', 'chalkboard-user', 'helmet-safety',
  'file-contract', 'file-signature', 'handshake', 'certificate', 'coins', 'window-maximize', 'puzzle-piece', 'layer-group', 'cubes', 'box', 'boxes-stacked', 'weight-scale', 'clock',
  'calendar-days', 'envelope', 'comment-sms', 'chart-line', 'list-check', 'clipboard-check', 'triangle-exclamation', 'heart-pulse', 'leaf', 'globe']

// Polish words of the trade, and what the font calls them.
const POLISH = {
  robot: 'display tablet', ekran: 'display tv', monitor: 'display desktop', komputer: 'computer desktop laptop', laptop: 'laptop', telefon: 'phone mobile', tablet: 'tablet',
  drukarka: 'print', kamera: 'camera video', monitoring: 'video camera eye', szlaban: 'road-barrier', brama: 'road-barrier dungeon', bramka: 'door-open road-barrier', drzwi: 'door',
  karta: 'id-card credit-card', identyfikator: 'id-badge id-card', dostęp: 'door-open key lock', kontrola: 'clipboard-check shield', klucz: 'key', zamek: 'lock', odcisk: 'fingerprint',
  kod: 'qrcode barcode', ochrona: 'shield user-shield', bezpieczeństwo: 'shield helmet-safety', bhp: 'helmet-safety', gwarancja: 'shield certificate', recepcja: 'bell-concierge',
  gość: 'user users', gości: 'users', pracownik: 'user-tie', osoba: 'user', kierowca: 'truck car', samochód: 'car', ciężarówka: 'truck', pojazd: 'car truck', parking: 'square-parking',
  budynek: 'building', biuro: 'building briefcase', fabryka: 'industry', zakład: 'industry', magazyn: 'warehouse boxes', paczka: 'box', chmura: 'cloud', serwer: 'server', sieć: 'network-wired wifi',
  prąd: 'plug bolt', zasilanie: 'plug bolt', usługa: 'headset screwdriver-wrench', usługi: 'headset screwdriver-wrench', serwis: 'screwdriver-wrench wrench', narzędzia: 'toolbox',
  wsparcie: 'headset life-ring', szkolenie: 'graduation-cap chalkboard-user', szkolenia: 'graduation-cap chalkboard-user', aplikacja: 'window-maximize mobile', aplikacje: 'window-maximize',
  moduł: 'puzzle-piece cubes', integracja: 'plug puzzle-piece', umowa: 'file-contract handshake', dokument: 'file', podpis: 'file-signature', licencja: 'certificate key',
  pieniądze: 'coins money-bill', cena: 'tag coins', waga: 'weight-scale', czas: 'clock', kalendarz: 'calendar', mapa: 'map location-dot', email: 'envelope', mail: 'envelope', sms: 'comment-sms',
  wiadomość: 'comment envelope', raport: 'chart-line file-lines', zdrowie: 'heart-pulse', alarm: 'bell triangle-exclamation', ostrzeżenie: 'triangle-exclamation', lista: 'list-check',
}

const fold = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l')
const name = computed(() => (props.modelValue || '').replace(/^fa-/, ''))

async function show() {
  if (props.disabled) return
  query.value = ''
  open.value = true
  if (!icons.value.length) icons.value = (await import('virtual:fa-icons')).default
  await nextTick()
  search.value?.focus()
}
function pick(icon) {
  emit('update:modelValue', icon ? `fa-${icon}` : null)
  open.value = false
}

const found = computed(() => {
  const words = fold(query.value).split(/\s+/).filter(Boolean)
  if (!words.length) return SUGGESTED.map((n) => icons.value.find((i) => i[0] === n)).filter(Boolean)
  // Each word: itself, and what the font calls it if it is a Polish word of the trade.
  const wanted = words.map((w) => [w, ...Object.entries(POLISH).filter(([pl]) => fold(pl).startsWith(w)).flatMap(([, en]) => en.split(' '))])
  const score = (i) => {
    let total = 0
    for (const alternatives of wanted) {
      let best = 0
      for (const w of alternatives) {
        if (i[0] === w) best = Math.max(best, 4)
        else if (i[0].startsWith(w)) best = Math.max(best, 3)
        else if (i[0].includes(w)) best = Math.max(best, 2)
        else if (fold(`${i[1]} ${i[2]}`).includes(w)) best = Math.max(best, 1)
      }
      if (!best) return 0
      total += best
    }
    return total
  }
  return icons.value.map((i) => [i, score(i)]).filter(([, s]) => s > 0).sort((a, b) => b[1] - a[1] || a[0][0].length - b[0][0].length).slice(0, 180).map(([i]) => i)
})
</script>

<template>
  <div class="flex items-center gap-2" data-testid="icon-picker">
    <button type="button" class="input flex w-auto min-w-48 items-center gap-2 text-left" :disabled="disabled" data-testid="icon-picker-open" @click="show">
      <span class="flex h-7 w-7 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:bg-brand-900/40">
        <i v-if="modelValue" :class="['fa-solid', modelValue]" aria-hidden="true"></i>
        <i v-else class="fa-regular fa-square text-faint" aria-hidden="true"></i>
      </span>
      <span :class="['flex-1 truncate text-sm', modelValue ? '' : 'text-faint']">{{ modelValue ? name : $t('iconPicker.choose') }}</span>
    </button>
    <UiButton v-if="modelValue && !disabled" size="xs" variant="ghost" icon="fa-xmark" :aria-label="$t('common.clear')" @click="pick(null)" />

    <UiModal :open="open" :title="$t('iconPicker.title')" size="lg" @close="open = false">
      <input ref="search" v-model="query" type="search" class="input mb-3" :placeholder="$t('iconPicker.search')" data-testid="icon-picker-search" @keydown.enter.prevent="found[0] && pick(found[0][0])" />
      <p class="mb-2 text-xs text-muted">{{ query.trim() ? $t('iconPicker.found', { n: found.length }) : $t('iconPicker.suggested') }}</p>
      <div v-if="icons.length" class="grid max-h-[55vh] grid-cols-[repeat(auto-fill,minmax(5.5rem,1fr))] gap-1.5 overflow-y-auto pr-1">
        <button v-for="i in found" :key="i[0]" type="button" :title="i[1]" :data-icon="i[0]"
                :class="['flex flex-col items-center gap-1 rounded-lg border p-2 text-center hover:border-brand-400 hover:bg-[var(--surface-3)]', modelValue === `fa-${i[0]}` ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30' : 'border-default']"
                @click="pick(i[0])">
          <i :class="['fa-solid text-xl', `fa-${i[0]}`]" aria-hidden="true"></i>
          <span class="w-full truncate text-3xs text-muted">{{ i[0] }}</span>
        </button>
      </div>
      <p v-else class="py-8 text-center text-sm text-faint"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i></p>
      <p v-if="icons.length && !found.length" class="py-6 text-center text-sm text-faint">{{ $t('common.nothingFound') }}</p>
    </UiModal>
  </div>
</template>
